import {
  ClientFeatureFlag,
  CustomFieldValue,
  PutApiV1ThreeLetterCodePartnerAdminsClientsClientIdBody,
} from '@fukuri-admins/apis/client/gen/model';
import { EditClientFormFieldValues } from '../schema';

type Args = {
  formValues: EditClientFormFieldValues;
  allFeatureFlags: ClientFeatureFlag[];
};

type IGenerateRequestBody = (args: Args) => PutApiV1ThreeLetterCodePartnerAdminsClientsClientIdBody;

/**
 * 全角数字を半角数字に変換する
 */
const toHalfWidthDigits = (str: string): string => {
  return str.replace(/[０-９]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xfee0));
};

/**
 * 文字列を number[] に変換する（全角数字対応）
 */
const parseOptionalNumberList = (val: string | undefined): number[] => {
  if (!val) return [];
  const normalized = toHalfWidthDigits(val);
  const parsed = Number.parseInt(normalized, 10);
  return Number.isNaN(parsed) ? [] : [parsed];
};

/**
 * customFields レコードを API 用の配列に変換する
 */
const formatCustomFieldValues = (
  customFields: Record<string, string | string[]> | undefined
): CustomFieldValue[] => {
  if (!customFields) return [];

  return Object.entries(customFields).map(([label, value]) => {
    const resolvedValue: string | number | boolean = Array.isArray(value) ? value.join(',') : value;
    return { label, value: resolvedValue };
  });
};

/**
 * 選択済みフィーチャーフラグを { feature_key: boolean } の形式に変換する
 * - selectedFlags に含まれるキーは true、含まれないキーは false となる
 */
const parseFeatureFlags = (
  selectedFlags: string[],
  allFlags: ClientFeatureFlag[]
): Record<string, boolean> => {
  return allFlags.reduce(
    (acc, flag) => {
      acc[flag.feature_key] = selectedFlags.includes(flag.feature_key);
      return acc;
    },
    {} as Record<string, boolean>
  );
};

/**
 * フォーム値からAPIリクエストボディを生成する
 */
export const generateRequestBody: IGenerateRequestBody = ({ formValues, allFeatureFlags }) => {
  const {
    name,
    contactPersonName,
    contactPersonEmail,
    contactPersonPhone,
    corporateNumber,
    externalClientId,
    groupId,
    employeeCount,
    customFields,
    clientFeatureFlags,
  } = formValues;

  return {
    client: {
      name,
      contact_person_name: contactPersonName,
      contact_person_email: contactPersonEmail,
      contact_person_phone: contactPersonPhone,
      corporate_number: corporateNumber || undefined,
      external_client_id: externalClientId || undefined,
      employee_count: employeeCount,
      group_ids: parseOptionalNumberList(groupId),
      custom_field_values: formatCustomFieldValues(customFields),
      client_feature_flags:
        clientFeatureFlags !== undefined && allFeatureFlags.length > 0
          ? parseFeatureFlags(clientFeatureFlags, allFeatureFlags)
          : undefined,
    },
  };
};
